import React, { useEffect, useState, useMemo } from "react";
import { BaseDraggable } from "../BaseDraggable";
import { useStore } from "../../../stores/store";
import { DraggableKeys } from "../../../interfaces/config";

import DetailsTab from "./DetailsTab";
import TaxInfoTab from "./TaxInfoTab";
import MiscTab from "./MiscTab";
import DocumentsTab from "./DocumentsTab";
// import InputMask from "react-input-mask";

const key = DraggableKeys.PARCEL_DETAILS_DRAGGABLE;
const currentYear = new Date().getFullYear() - 1;
export default function ParcelDetailsDraggable() {
  const {
    isActive,
    toggleActiveItem,
    detailsSelection,
    getDetailsByPin,
    setDetailsSelection,
  } = useStore();
  const [data, setData] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("details");
  const [pinInput, setPinInput] = useState("");
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const tabs = useMemo(
    () => [
      {
        id: "details",
        label: "Details",
        component: DetailsTab,
      },
      {
        id: "taxInfo",
        label: "Tax Info",
        component: TaxInfoTab,
      },
      {
        id: "misc",
        label: "Misc",
        component: MiscTab,
      },
      {
        id: "documents",
        label: "Documents",
        component: DocumentsTab,
      },
    ],
    []
  );

  useEffect(() => {
    if (!detailsSelection) {
      return;
    }
    setPinInput(detailsSelection.ParcelNumber);
    setIsLoading(true);

    (async () => {
      const rawData = await getDetailsByPin(detailsSelection.ParcelNumber);
      
      // Temporarily added a suppression of the NetTaxableValue & TotalTax values for 2025 until further direction is provided
      const cleanedData = {
        ...rawData,
        Parcels: (rawData.Parcels ?? []).map((parcel: any) => {
          return parcel.Key == 2025
            ? {
              ...parcel,
              Value: {
                ...parcel.Value,
                NetTaxableValue: "",
                TaxRate: "",
                TotalTax: ""
              }
            }
            : parcel;
        })
      };
      
      setData(cleanedData);
      setIsLoading(false);
      setSelectedYear(currentYear);
    })();
  }, [detailsSelection]);

  const selectedParcel = useMemo(() => {
    if (!data || !data.Parcels) return null;
    const _selectedYearData = data.Parcels.find((parcel) => parcel.Key === selectedYear);

    if (_selectedYearData) {
      return _selectedYearData.Value;
    } else {
      // If the selected year data is not found, return the first available parcel
      if (data.Parcels.length > 0) {
        setSelectedYear(data.Parcels[0].Key);
      }
      return null;
    }
  }, [data, selectedYear]);

  const isPinInputValid = useMemo(() => {
    const _pinInput = pinInput.replace(new RegExp(" ", "g"), "");
    return _pinInput.length === 13;
  }, [pinInput]);

  return (
    <BaseDraggable
      id={key}
      onClose={() => toggleActiveItem(key)}
      onBack={() => {
        toggleActiveItem(key); toggleActiveItem(DraggableKeys.RESULTS_DRAGGABLE)

      }}
      visible={isActive(key)}
      initialPosition="top-right"
      headerText="PARCEL DETAILS"
      size="md"
    >
      <div className="w-full flex items-center space-betweeng">
        <div className="flex items-center">
          <div className="w-10">PIN:</div>
          {/* <InputMask
            mask="99-99-999-999"
            maskChar=" "
            value={pinInput}
            onChange={(e) => setPinInput(e.target.value)}
            className="input input-bordered input-xs"
          /> */}
        </div>
        <div className="flex-1 flex- flex items-center justify-start">
          <button
            disabled={isLoading || !isPinInputValid}
            onClick={() => {
              setDetailsSelection({ ParcelNumber: pinInput });
            }}
            className={`btn-athena md:w-20 md:px-0 btn-xs ml-2 h-auto ${isLoading || !isPinInputValid
              ? "opacity-50 cursor-not-allowed"
              : ""
              }`}
          >
            Get Details
          </button>
          <button
            title="More Info"
            onClick={() => {
              const pin = pinInput.replace(new RegExp("-", "g"), "");
              const wedgeUrl = `https://mchenryil.devnetwedge.com/parcel/view/${pin}/${selectedYear}`;
              window.open(wedgeUrl, "_blank");
            }}
            className={`btn-athena md:w-20 md:px-0 ml-2 btn-xs h-auto ${!isPinInputValid ? "opacity-50 cursor-not-allowed" : ""
              }`}
          >
            More Info
          </button>
        </div>
      </div>
      {
        isLoading ? (
          <>
            <div className="p-2">Loading...</div>
            <progress className="progress progress-info w-full"></progress>
          </>
        ) : (
          <>
            {!selectedParcel ? (
              <div className="p-2 w-full bg-red-200 border-red-500 text-red-700 mt-2">
                No data available. Please try a different parcel number.
              </div>
            ) : (
              <>
                <div className="w-full flex items-center mt-2 mb-4">
                  <span className="w-10">Year:</span>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                    className="select select-xs select-bordered"
                  >
                    {data?.Parcels?.sort((a, b) => b.Key - a.Key).map(
                      ({ Key }) => {
                        return (
                          <option key={Key} value={Key}>
                            {Key} - Payable {Key + 1}
                          </option>
                        );
                      }
                    )}
                  </select>
                </div>
                <div role="tablist" className="tabs tabs-lifted">
                  {tabs.map((tab) => (
                    <a
                      key={tab.id}
                      role="tab"
                      className={`rounded-none hover:bg-gray-100 tab ${activeTab === tab.id ? "tab-active" : ""
                        }`}
                      onClick={() => setActiveTab(tab.id)}
                    >
                      {tab.label}
                    </a>
                  ))}
                </div>
                <div className="border-x border-b">
                  {tabs.map((tab) => {
                    return (
                      <div
                        className={`${activeTab === tab.id ? "" : "hidden"
                          } p-2 max-h-96 overflow-y-auto`}
                        key={tab.id}
                        role="tabpanel"
                      >
                        <tab.component data={selectedParcel} />
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )
      }
    </BaseDraggable >
  );
}
